import {useEffect, useRef, useState} from 'react';
import {Box, TextField} from '@mui/material';
import PropTypes from 'prop-types';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';
import {stripHtmlTags} from './AdditionalNotes';

const NumericInputBox = ({
                             index,
                             id,
                             priority,
                             onChange,
                             description,
                             isEditMode,
                             enableValidation,
                             isFormArchived,
                             readonly,
                             placeholderValues,
                             accessLevel,
                             onUpdateDescription,
                             componentToEditIndex,
                             submitMode,
                             error,
                             showEditModeOnly = false,
                             fieldKey = '',
                             value
                         }) => {
    const [currentValue, setCurrentValue] = useState(description);
    const [inputValue, setInputValue] = useState('');
    const prevPropRef = useRef();
    useEffect(() => {
        setCurrentValue(description);
    }, [description]);

    useEffect(() => {
        const next = priority ?? value ?? '';
        setInputValue(next.toString());
    }, [priority, value]);

    useEffect(() => {
        if (prevPropRef.current === index && componentToEditIndex !== prevPropRef.current) {
            const num = inputValue === '' ? undefined : Number(inputValue);
            onUpdateDescription({
                index,
                description: stripHtmlTags(currentValue),
                formattedDescription: currentValue,
                ...(fieldKey && showEditModeOnly ? {priority: num} : {}),
            });
        }
        prevPropRef.current = componentToEditIndex;
    }, [componentToEditIndex]);

    const handleNumericChange = (e) => {
        const raw = e.target.value;
        setInputValue(raw);

        const num = raw === '' ? '' : Number(raw);
        if (isEditMode) {
            onUpdateDescription({
                index,
                priority: num
            });
        } else {
            onChange({
                id,
                value: num
            });
        }
    };

    if (showEditModeOnly && submitMode) return null;

    return (
        <Box key={`NumericInputBox-${id}`} sx={{mt: 2}}>
            {isEditMode
                ? (
                    <Box sx={{mb: 1}}>
                        <RichEditor
                            value={description}
                            onChange={onChange}
                            index={index}
                            isEditMode
                            isFormLive={isFormArchived}
                            accessLevel={accessLevel}
                            currentDescription={currentValue}
                            setCurrentDescription={setCurrentValue}
                        />
                    </Box>
                )
                : (
                    <RichEditorResultText
                        id={id}
                        description={description}
                        placeholderValues={placeholderValues}
                        readonly={readonly}
                        enableValidation={enableValidation}
                    />
                )}

            {!(isEditMode && !showEditModeOnly) && (
                (!readonly && ((showEditModeOnly && isEditMode) || submitMode))
                    ? (
                        <TextField
                            fullWidth
                            required={enableValidation && !showEditModeOnly}
                            type="number"
                            value={inputValue}
                            onChange={handleNumericChange}
                            disabled={!submitMode && !isEditMode}
                            error={(fieldKey && !!error) || error}
                            helperText={error}
                            inputProps={{
                                step: 'any',
                                inputMode: 'numeric',
                                autoComplete: 'off',
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'e' || e.key === 'E') e.preventDefault();
                            }}
                            onPaste={(e) => {
                                const text = (e.clipboardData || window.clipboardData).getData('text');
                                if (/[eE.]/.test(text)) e.preventDefault();
                            }}
                            sx={{mt: 1}}
                        />
                    )
                    : (
                        <TextField
                            fullWidth
                            value={inputValue}
                            InputProps={{readOnly: true}}
                            disabled
                            sx={{
                                mt: 1,
                                '& .MuiInputBase-input.Mui-disabled': {
                                    WebkitTextFillColor: 'black',
                                    color: 'black',
                                },
                            }}
                        />
                    )
            )}

            {isEditMode && (
                <Box sx={{mt: 1}}>
                    <RequiredCheckbox
                        enableValidation={fieldKey ? true : enableValidation}
                        index={index}
                        onChange={fieldKey
                            ? () => {
                            }
                            : (showEditModeOnly
                                ? () => {
                                }
                                : onChange)}
                        isFormArchived={isFormArchived || fieldKey !== ''}
                        checked={fieldKey ? true : undefined}
                        disabled={fieldKey ? true : undefined}
                    />
                </Box>
            )}
        </Box>
    );
};

NumericInputBox.propTypes = {
    id: PropTypes.string,
    priority: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    index: PropTypes.number,
    description: PropTypes.string,
    enableValidation: PropTypes.bool,
    onChange: PropTypes.func,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readonly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    onUpdateDescription: PropTypes.func,
    componentToEditIndex: PropTypes.number,
    submitMode: PropTypes.bool,
    error: PropTypes.string,
    showEditModeOnly: PropTypes.bool,
    fieldKey: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default NumericInputBox;
